import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ritornoDa } from "@/lib/ritorno";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import AuthShell, { Avviso, TestataModulo } from "@/components/auth/AuthShell";
import { CodiceSei } from "@/components/auth/CampiAccesso";
import { ShieldCheck } from "lucide-react";

export default function TotpChallengePage() {
  const nav = useNavigate();
  const ritorno = ritornoDa(useLocation().search); // la pagina da cui si è partiti (la pagina di consenso di Claude)
  const { session, aal, factors, refreshAal, signOut } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);
  const verificato = aal?.currentLevel === "aal2";

  useEffect(() => {
    if (!session) nav("/login", { replace: true });
  }, [session, nav]);

  /* Dopo «Verifica e continua» la sessione nuova fa rileggere il livello al contesto, e la rilettura lanciata da
     `submit` viene scartata: si arrivava a «/» col livello vecchio e il cancello rimandava qui. Si entra appena il
     contesto sa che la sessione è verificata (prima, con i tempi di una persona, serviva il codice due volte). */
  useEffect(() => {
    if (verificato) nav(ritorno || "/", { replace: true });
  }, [verificato, nav, ritorno]);

  const submit = async () => {
    const factor = (factors?.totp || []).find((f) => f.status === "verified");
    if (!factor) {
      setErrore("Su questo accesso non c'è nessun codice di verifica attivo. Esci e rientra, o chiedi alla direzione.");
      return;
    }
    setErrore(null);
    setBusy(true);
    const { data: ch, error: ce } = await supabase.auth.mfa.challenge({
      factorId: factor.id,
    });
    if (ce) {
      setBusy(false);
      setErrore("Non riesco a controllare il codice. Controlla la connessione e riprova.");
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: factor.id,
      challengeId: ch.id,
      code,
    });
    setBusy(false);
    if (error) {
      setErrore("Codice non valido o già scaduto. Scrivi quello che vedi adesso nell'app.");
      setCode("");
      return;
    }
    await refreshAal(); // si entra dall'effetto qui sopra, appena il livello è aal2
  };

  const email = session?.user?.email;

  return (
    <AuthShell testId="codice-pagina">
      <div className="flex flex-col gap-[18px]">
        <TestataModulo titolo="Codice di verifica" icona={ShieldCheck}>
          Apri l&#39;app di autenticazione sul telefono e scrivi le 6 cifre della voce «Interni D&#39;Autore» (se non la
          trovi, cerca gestionale.internidautore.com).
        </TestataModulo>

        {errore ? <Avviso data-testid="totp-challenge-errore">{errore}</Avviso> : null}
        {verificato ? <Avviso tono="ok" data-testid="totp-challenge-ok">Verificato. Ti porto dentro…</Avviso> : null}

        <div data-testid="totp-challenge-input">
          <CodiceSei value={code} onChange={setCode} invalido={Boolean(errore)} autoFocus aria-label="Codice a 6 cifre dell'app di autenticazione" />
        </div>
        <p className="text-small text-ink-2">Il codice cambia ogni 30 secondi: se scade mentre scrivi, usa quello nuovo.</p>

        <Button onClick={submit} size="xl" loading={busy} loadingText="Verifica…" disabled={code.length < 6} data-testid="totp-challenge-submit-btn" className="w-full max-md:h-[54px]">
          Verifica e continua
        </Button>

        <div className="flex flex-col gap-1 text-ink-2">
          <p>
            {email ? <>Non sei <strong className="font-semibold text-ink">{email}</strong>? </> : null}
            <button type="button" onClick={signOut} className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px] md:min-h-0" data-testid="totp-challenge-cancel-btn">
              Esci
            </button>
          </p>
          <Link to="/aiuto/entrare" className="inline-flex min-h-11 items-center font-semibold text-brand-ink underline underline-offset-[3px]" target="_blank" rel="noopener noreferrer" data-testid="aiuto-link">
            Le cifre non vanno? Ecco come si entra
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
